import {
  closest,
  getWindow,
  NORMAL_NODE,
} from '@packages/shared/utilities/DomUtils';
import {getWheelDirection} from './getWheelDirection';
import {isScrollableInDirection} from './isScrollableInDirection';

/**
 * Gets the closest parent native element that has an overflown height
 *
 * @param  {DOMEventTarget}    event         The native DOM event target.
 * @return {DOMElement}                      If the passed element or one of its parents has an overflown property and
 *                                           if the element is still scrollable on the current mousewheel direction
 *                                           it'll return that node. Otherwise, returns null.
 */
const closestScrollableElement = (event: Event): HTMLElement | null => {
  const target = event.target;
  if (!target) {
    return null;
  }
  const win = getWindow(target);

  if (!win) return null;

  const closestOverflowElement = closest(target, (element) => {
    if (element.nodeType !== NORMAL_NODE) {
      return false;
    }

    const el = element as Element;

    // The outermost <HTML> overflow is set to `hidden` by default.
    // If we are here, it means we walked all the way up the DOM tree.
    if (el.tagName === 'HTML') {
      return true;
    }

    const computedStyles = win.getComputedStyle(el);
    const {overflowY} = computedStyles;
    return overflowY === 'scroll' || overflowY === 'auto';
  });

  // The outermost <HTML> is always scrollable by default, so we want to ignore it here
  if (!closestOverflowElement) {
    return null;
  }

  if (
    !(
      closestOverflowElement.nodeType === Node.ELEMENT_NODE &&
      'tagName' in closestOverflowElement
    )
  ) {
    return null;
  }

  const htmlElement = closestOverflowElement as HTMLElement;

  // The outermost <HTML> is always scrollable by default, so we want to ignore it here
  if (htmlElement.tagName === 'HTML') {
    return null;
  }

  return htmlElement;
};

/**
 * If the mouse is active over a scrollable element (for example, an element on the canvas with overflow set to scroll),
 * we check the scroll direction and whether that element can in fact continue in that direction and has not been scrolled to the end.
 */
export const isScrollingOverScrollableElement = (
  event: WheelEvent
): boolean => {
  const maybeClosestScrollableElem = closestScrollableElement(event);
  if (maybeClosestScrollableElem) {
    const direction = getWheelDirection(event);
    if (
      !direction ||
      isScrollableInDirection(maybeClosestScrollableElem, direction)
    ) {
      return true;
    }
  }

  return false;
};
